import type { AskFrame, AskMessage, AskPayload, AskReference } from "@/lib/ask";
import type { Definition, GrammarEntry } from "@/lib/search";

/**
 * Turning a thread — stored and in flight — into rows the transcript draws (日本-325).
 *
 * Pure, and out of the component for `logTabs.ts`'s and `libraryRows.ts`'s reason: this is where
 * the ordering and the fold-a-frame-into-a-turn rules live, and both are testable without a DOM
 * while a component holding them is not.
 */

export type AskRow =
  | { kind: "question"; id: string; text: string; references: AskReference[] }
  | { kind: "answer"; id: string; text: string; reasoning: string }
  | {
      kind: "tool";
      id: string;
      name: string;
      summary: string;
      payload: AskPayload | null;
      ok: boolean;
      pending: boolean;
    };

export interface LiveTurn {
  rows: AskRow[];
  busy: boolean;
  error: string | null;
  /** What the last answer cost. Drawn only when a turn dropped history — see `droppedTurns`. */
  droppedTurns: number;
}

export const IDLE: LiveTurn = { rows: [], busy: false, error: null, droppedTurns: 0 };

/** `砂漠に仙人掌が生…` — enough to recognise the sentence, short enough for a 34px chip. */
export function clip(text: string, limit = 18): string {
  return text.length <= limit ? text : `${text.slice(0, limit - 1)}…`;
}

/**
 * The label on a reference chip.
 *
 * The ordinal is the sentence's place in the log *now*, which is why it is passed rather than
 * stored on the reference: a sentence moved between logs keeps its text and loses its position, and
 * a chip claiming `#24` for a sentence that is now third would be worse than one claiming nothing.
 */
export function chipLabel(reference: AskReference, ordinal?: number): string {
  const head = ordinal === undefined ? "" : `#${ordinal} `;
  return `${head}${clip(reference.text)}`;
}

/** Attach a chip, ignoring one that is already attached. Several are allowed; duplicates are not —
 * the same sentence twice says nothing the once did not. */
export function addChip(
  chips: AskReference[],
  reference: AskReference,
): AskReference[] {
  return chips.some((c) => c.sentenceId === reference.sentenceId) ? chips : [...chips, reference];
}

export function removeChip(chips: AskReference[], sentenceId: string): AskReference[] {
  return chips.filter((c) => c.sentenceId !== sentenceId);
}

/** Stored messages as rows. A tool call on an assistant turn draws nothing of its own — the tool
 * row that follows it carries the line — so an assistant turn with no text is dropped rather than
 * rendering an empty bubble. */
export function toRows(messages: AskMessage[]): AskRow[] {
  const rows: AskRow[] = [];
  for (const message of messages) {
    if (message.role === "user") {
      rows.push({
        kind: "question",
        id: message.id,
        text: message.text,
        references: message.references,
      });
    } else if (message.role === "assistant") {
      // An assistant turn with neither text nor reasoning draws nothing — it exists only to carry
      // the tool calls, and the tool rows below it already say what happened. One that has
      // *reasoning* and no text is kept: on a tool-calling round that thinking is the only account
      // of why the tool was chosen, and dropping it is how a model looks like it acted at random.
      if (message.text.trim() || message.reasoning?.trim()) {
        rows.push({
          kind: "answer",
          id: message.id,
          text: message.text,
          reasoning: message.reasoning ?? "",
        });
      }
    } else {
      rows.push({
        kind: "tool",
        id: message.id,
        name: message.name,
        summary: message.summary,
        payload: message.payload,
        ok: message.ok !== false,
        pending: false,
      });
    }
  }
  return rows;
}

/**
 * Fold one streamed frame into the turn in flight.
 *
 * Returns a new object rather than mutating, so React re-renders — the same discipline every other
 * reducer-shaped helper in this surface keeps.
 *
 * **A tool call draws its line immediately, pending, and the result replaces it in place.** Waiting
 * for the result would leave the transcript silent for the whole of a lookup, which reads as the
 * answer having stopped.
 */
export function applyFrame(live: LiveTurn, frame: AskFrame): LiveTurn {
  switch (frame.type) {
    case "text": {
      const rows = [...live.rows];
      const last = rows[rows.length - 1];
      if (last && last.kind === "answer") {
        rows[rows.length - 1] = { ...last, text: last.text + frame.text };
      } else {
        rows.push({ kind: "answer", id: `live-${rows.length}`, text: frame.text, reasoning: "" });
      }
      return { ...live, rows };
    }
    case "reasoning": {
      // Folded into the same answer row the text goes to, not a row of its own. The disclosure sits
      // *above* the answer it explains, so one row owning both is what keeps them together when the
      // turn is re-read from the store — where they are one message.
      const rows = [...live.rows];
      const last = rows[rows.length - 1];
      if (last && last.kind === "answer") {
        rows[rows.length - 1] = { ...last, reasoning: last.reasoning + frame.text };
      } else {
        rows.push({ kind: "answer", id: `live-${rows.length}`, text: "", reasoning: frame.text });
      }
      return { ...live, rows };
    }
    case "toolCall":
      return {
        ...live,
        rows: [
          ...live.rows,
          {
            kind: "tool",
            id: frame.callId,
            name: frame.name,
            summary: `${frame.name}…`,
            payload: null,
            ok: true,
            pending: true,
          },
        ],
      };
    case "toolResult": {
      const rows = live.rows.map((row) =>
        row.kind === "tool" && row.id === frame.callId
          ? {
              ...row,
              id: frame.id,
              summary: frame.summary,
              payload: frame.payload,
              ok: frame.ok,
              pending: false,
            }
          : row,
      );
      return { ...live, rows };
    }
    case "translation":
      // Not a row. The English lands on the sentence in the log above; drawing it here too would
      // be the transcript claiming a translation it does not own.
      return live;
    case "done":
      return { ...live, busy: false, droppedTurns: frame.droppedTurns };
    case "error":
      return { ...live, busy: false, error: frame.message };
  }
}

export const PRESENT_TOOL = "present_definitions";
/** The DoJG pair (日本-519). `grammar` is the model's reference; `present_grammar` is the reader's. */
export const GRAMMAR_TOOL = "grammar";
export const PRESENT_GRAMMAR_TOOL = "present_grammar";

/**
 * Whether a tool row's `entries` are DoJG entries rather than dictionary rows (日本-519).
 *
 * **Keyed on the tool name, because the payload carries no discriminator** — the server already
 * distinguishes the two by which tool was called. It is not cosmetic: a `GrammarEntry` has
 * `concept` and `examples` and no `senses`, so a grammar payload reaching `ResultCard` throws on
 * `entry.senses.map` rather than drawing something wrong.
 */
export function isGrammarTool(row: AskRow): boolean {
  return row.kind === "tool" && (row.name === GRAMMAR_TOOL || row.name === PRESENT_GRAMMAR_TOOL);
}

/** The DICTIONARY entries a tool row carries. Empty for a grammar row — see `isGrammarTool`. */
export function entriesOf(row: AskRow): Definition[] | undefined {
  if (row.kind !== "tool" || isGrammarTool(row)) return undefined;
  return (row.payload?.entries as Definition[] | undefined) ?? undefined;
}

/** The GRAMMAR entries a tool row carries. Empty for anything but the DoJG pair. */
export function grammarEntriesOf(row: AskRow): GrammarEntry[] | undefined {
  if (row.kind !== "tool" || !isGrammarTool(row)) return undefined;
  return (row.payload?.entries as GrammarEntry[] | undefined) ?? undefined;
}

/**
 * Whether a tool row's cards are part of the answer, or the model's working.
 *
 * **Looking a word up and showing it to the reader are two different acts.** `dict` is the model's
 * own reference — it calls it freely, including for words it then discards — so its result draws as
 * one line the reader can open to check the working. `present_definitions` is the model saying
 * *this* entry belongs in the explanation, so those cards are drawn in full.
 *
 * Keyed on the tool name rather than on a flag in the payload: the server already distinguishes
 * them by which tool was called, and a second spelling of that fact is a second thing to keep in
 * step.
 */
export function isPresentation(row: AskRow): boolean {
  return (
    row.kind === "tool" && (row.name === PRESENT_TOOL || row.name === PRESENT_GRAMMAR_TOOL)
  );
}

export const VISIBLE_ENTRIES = 3;
/**
 * How many dictionary cards a tool row draws before the rest fold away.
 *
 * A common word resolves to a dozen entries and each renders a full `ResultCard`, so an unfolded
 * lookup buries the answer it was made for under a page of cards the reader did not ask for — and
 * the *next* question, further down, off the bottom of the dock entirely.
 *
 * Three rather than one, because the first entry is often not the right sense and a fold that hides
 * the answer is a fold nobody trusts. Deliberately **not** `MAX_DICT_ENTRIES` (6, what the model is
 * sent): those two numbers answer different questions — one is what a budget can afford, this is
 * what a 700px column can show — and tying them together would make a token decision move the UI.
 */

/** The entries to draw, and how many are being held back. */
export function splitEntries<T>(
  entries: readonly T[] | undefined,
  expanded: boolean,
): { shown: T[]; hidden: number } {
  if (!entries || entries.length === 0) return { shown: [], hidden: 0 };
  if (expanded || entries.length <= VISIBLE_ENTRIES) return { shown: [...entries], hidden: 0 };
  return {
    shown: entries.slice(0, VISIBLE_ENTRIES),
    hidden: entries.length - VISIBLE_ENTRIES,
  };
}
